import { addHours, format, parseISO } from "date-fns";
import { formatInUTC } from "./formatInUTC";

const HAS_UTC_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/** Parses a PIT string as UTC. PITs without an offset are UTC (never browser-local). */
export const parsePitAsUtc = (pit: string) =>
  parseISO(HAS_UTC_OFFSET.test(pit) ? pit : `${pit}Z`);

/**
 * A Date whose *local* fields equal `instant`'s UTC fields, so local-time formatters
 * (date-fns `format`, d3 `timeFormat`) print the UTC wall clock in any browser zone.
 * Built with a plain `new Date(...)`, NOT a Date subclass (e.g. `UTCDate`): Playwright's
 * `page.clock` swaps `Date` for a fake whose subclasses lose their overrides.
 * Caveat: a UTC wall time that does not exist in the host zone (its spring-forward hour)
 * is moved forward by an hour.
 */
export const utcFieldsAsLocalDate = (instant: Date) =>
  new Date(
    instant.getUTCFullYear(),
    instant.getUTCMonth(),
    instant.getUTCDate(),
    instant.getUTCHours(),
    instant.getUTCMinutes(),
    instant.getUTCSeconds(),
    instant.getUTCMilliseconds()
  );

export const dateToPit = (date: Date | number) =>
  format(date, "yyyy-MM-dd'T'HH:mm:ss");

// Truncate PIT string to remove microseconds and timezone info
export const formatPitForDisplay = (pit: string | undefined) => {
  if (!pit) return "";
  // Match YYYY-MM-DDTHH:MM:SS and ignore high-res pits with microseconds
  const match = pit.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/);
  return match ? match[1] : pit;
};

export const pitToHumanReadable = (
  pit: string | Date | undefined,
  shouldSplit = false,
  format: Intl.DateTimeFormatOptions = {},
  // Display offset (hours from UTC) from the timezone selector. Pass for real
  // event times; omit (0) for logical PIT labels, which stay literal/UTC.
  tz = 0
) => {
  if (!pit) return "";

  // Ensure PIT strings are always treated as UTC
  let date: Date;
  if (pit instanceof Date) {
    date = pit;
  } else {
    date = parsePitAsUtc(pit);
  }
  // Apply the display offset on top of the UTC instant, then format in UTC so
  // the result is independent of the browser's own timezone.
  if (tz) date = addHours(date, tz);
  const currentYear = new Date().getFullYear();
  const pitYear = date.getFullYear();
  const isCurrentYear = pitYear === currentYear;

  const dateFormatOptions: Intl.DateTimeFormatOptions = {
    month: "short",
    year: isCurrentYear ? undefined : "2-digit",
    day: "numeric",
  };

  // check if the `format` argument explicitly sets the "date" options as "undefined"
  const undefinedYearFormat = "year" in format && format.year === undefined;
  const undefinedMonthFormat = "month" in format && format.month === undefined;
  const undefinedDayFormat = "day" in format && format.day === undefined;

  // `date` is already tz-adjusted above (needed for the year calc); pass NO tz to
  // formatInUTC or the offset double-applies.
  if (!shouldSplit) {
    return formatInUTC(date, {
      // weekday: "short",
      ...dateFormatOptions,
      hour: "numeric",
      minute: "numeric",
      ...format,
    });
  }

  const datePart = formatInUTC(date, { ...dateFormatOptions });

  const timePart = formatInUTC(date, {
    hour: "numeric",
    minute: "numeric",
    ...format,
  });

  if (undefinedYearFormat && undefinedMonthFormat && undefinedDayFormat) {
    return timePart;
  }

  return `${datePart}\n${timePart}`;
};
